"use client";

import Image from "next/image";
import Link from "next/link";
import { motion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowUpRight } from "@/components/icons";
import type { HomeBlogPost } from "./fetch";

const DEFAULT_CATEGORY_COLOR = "color-mix(in srgb, var(--foreground) 55%, transparent)";

export const categoryColor: Record<string, string> = {
  Guide: DEFAULT_CATEGORY_COLOR,
  Tutorial: "#5b8cff",
  Research: "#f59e0b",
  // was cyan #06b6d4 — retired hue, moved onto the secondary accent
  AI: "#ff8a5b",
  Announcement: "#ec4899",
};

const tint = (color: string, pct: number) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;

type BlogPostCardProps = {
  post: HomeBlogPost;
  index: number;
  prefersReducedMotion: boolean | null;
  /**
   * Cloned cards in the marquee loop skip the scroll reveal (they must be
   * visible the moment the seam comes around) and stay out of the tab order.
   */
  duplicate?: boolean;
};

export function BlogPostCard({
  post,
  index,
  prefersReducedMotion,
  duplicate = false,
}: BlogPostCardProps) {
  const color = categoryColor[post.category] ?? DEFAULT_CATEGORY_COLOR;
  const reveal = !duplicate && !prefersReducedMotion;

  return (
    <motion.article
      initial={reveal ? { opacity: 0, y: 16 } : false}
      whileInView={reveal ? { opacity: 1, y: 0 } : undefined}
      viewport={scrollRevealViewport}
      transition={{ delay: index * 0.06, duration: 0.45 }}
      className="h-full"
    >
      <Link
        href={post.href}
        aria-label={`Read article: ${post.title}`}
        tabIndex={duplicate ? -1 : undefined}
        className="group border-border bg-card focus-visible:ring-brand-green/50 relative flex h-full flex-col overflow-hidden rounded-2xl border outline-none focus-visible:ring-2"
      >
        {/* Full cover — native 1200×628 ratio, shown in full (no cropping) */}
        <div className="bg-surface-2 relative aspect-1200/628 w-full overflow-hidden">
          {post.image ? (
            <Image
              src={post.image}
              alt={post.title}
              fill
              sizes="(max-width: 640px) 300px, 340px"
              className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
            />
          ) : (
            <>
              <div
                className="absolute inset-0"
                style={{
                  background: `radial-gradient(ellipse 70% 70% at 22% 18%, ${tint(color, 32)}, transparent 70%), radial-gradient(ellipse 65% 65% at 88% 92%, color-mix(in srgb, var(--brand-green) 15%, transparent), transparent 70%)`,
                }}
                aria-hidden="true"
              />
              <span
                aria-hidden="true"
                className="text-foreground/10 absolute inset-0 flex items-center justify-center px-6 text-center text-[26px] font-bold tracking-tight"
              >
                {post.category}
              </span>
            </>
          )}

          {/* Hover affordance */}
          <span
            aria-hidden="true"
            className="bg-background/80 text-foreground absolute top-3 right-3 flex h-8 w-8 translate-y-1 items-center justify-center rounded-full opacity-0 backdrop-blur-md transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100"
          >
            <ArrowUpRight size={15} />
          </span>
        </div>

        {/* Minimal caption */}
        <div className="flex flex-1 flex-col p-4 sm:p-5">
          <div
            className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wide"
            style={{ color }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
            {post.category}
          </div>

          <h3 className="text-foreground group-hover:text-brand-green mt-2 line-clamp-2 text-[16px] leading-snug font-semibold tracking-[-0.01em] transition-colors duration-200">
            {post.title}
          </h3>

          <div className="mt-auto flex items-center gap-2.5 pt-5">
            {post.authorImage ? (
              <Image
                src={post.authorImage}
                alt={post.author}
                width={28}
                height={28}
                className="h-7 w-7 shrink-0 rounded-full object-cover"
              />
            ) : (
              <span
                aria-hidden="true"
                className="text-brand-green flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
                style={{
                  backgroundColor: "color-mix(in srgb, var(--brand-green) 14%, transparent)",
                }}
              >
                {post.authorInitial}
              </span>
            )}
            <div className="min-w-0">
              <div className="text-foreground truncate text-xs leading-tight font-medium">
                {post.author}
              </div>
              <div className="text-muted-foreground text-[11px]">
                {post.date} · {post.readTime}
              </div>
            </div>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}
